import {
  Badge,
  Box,
  Flex,
  List,
  ListItem,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useAllAppointment } from "api/appointments";
import { add, format } from "date-fns";
import DateRangeInput from "generics/DateRangeInput";
import Link from "generics/Link";
import PageTitle from "generics/PageTitle";
import { SearchInput } from "generics/SearchInput";
import ToolbarPortal from "generics/ToolbarPortal";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import React, { useState } from "react";
import { useForm } from "react-hook-form";

export default function AllAppointmentsPage() {
  const [form, setForm] = useState<{ start: Date; end: Date }>({
    end: add(new Date(), { weeks: 1 }),
    start: new Date(),
  });
  const { register, handleSubmit } = useForm({
    defaultValues: {
      start: form.start,
      end: form.end,
    },
  });

  const appointmentsQuery = useAllAppointment({
    start: form.start,
    end: form.end,
  });

  const [filterTerm, setFilterTerm] = useState("");
  const debouncedOnChange = useDebouncedCallback(
    (e) => setFilterTerm(e.target.value),
    400
  );

  let appointments =
    appointmentsQuery.data?.filter((appointment: any) =>
      appointment.user?.name.toLowerCase().includes(filterTerm.toLowerCase())
    ) || [];

  function getResult(patient: any) {
    if (patient.status === "completed")
      return <Badge colorScheme="green">Published</Badge>;
    const completedTests = Object.values(patient.tests).filter(
      (test: any) => test.status === "completed"
    );
    if (completedTests.length === Object.values(patient.tests).length)
      return <Badge colorScheme="green">Published</Badge>;
    if (completedTests.length === 0)
      return <Badge colorScheme="red">Not Published</Badge>;
    return (
      <Stack direction="row">
        <Badge colorScheme="green">{completedTests.length} Published</Badge>
        <Badge colorScheme="red">
          {Object.values(patient.tests).length - completedTests.length}{" "}
          Remaining
        </Badge>
      </Stack>
    );
  }

  return (
    <Box px={12} py={6}>
      <ToolbarPortal>
        <Flex justifyContent="space-between" alignItems="center">
          <PageTitle flexGrow={1}>All Appointments</PageTitle>
          <Stack direction="row">
            <DateRangeInput
              size="sm"
              defaultValues={{
                startDate: form.start,
                endDate: form.end,
              }}
              onClose={({ startDate, endDate }: any) =>
                setForm({ start: startDate, end: endDate })
              }
            />
            <SearchInput size="sm" onChange={(e) => debouncedOnChange(e)} />
          </Stack>
        </Flex>
      </ToolbarPortal>
      {/* <form onSubmit={handleSubmit((v) => setForm(v))}>
        <Stack direction="row" align="center">
          <Input
            type="date"
            name="start"
            bg="white"
            maxW="200px"
            id="start"
            ref={register}
          />
          <Text>to</Text>
          <Input
            type="date"
            name="end"
            bg="white"
            maxW="200px"
            id="end"
            ref={register}
          />
          <Button type="submit">Filter</Button>
        </Stack>
      </form> */}

      <Table>
        <Thead>
          <Tr>
            <Th>USER</Th>
            <Th>STATUS</Th>
            <Th>DATE</Th>
            <Th>Patient Names</Th>
            <Th>Tests</Th>
            <Th>Results</Th>
          </Tr>
        </Thead>
        <Tbody>
          {appointmentsQuery.isLoading || !appointmentsQuery.data ? (
            <Tr>
              <Td colSpan={6}>
                <Text textAlign="center" color="gray.500">
                  <Spinner />
                </Text>
              </Td>
            </Tr>
          ) : appointments.length === 0 ? (
            <Tr>
              <Td colSpan={6}>
                <Text textAlign="center" color="gray.500">
                  No Appointments!
                </Text>
              </Td>
            </Tr>
          ) : (
            appointments.map((appointment: any) => {
              return (
                <Tr fontSize="sm" key={appointment.id}>
                  <Td>
                    <Link to={`/appointments/${appointment.id}`}>
                      {`${appointment.user.name}: #${appointment.user.readableId}`}
                    </Link>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">
                      {appointment.status.split("_").join(" ")}
                    </Badge>
                  </Td>
                  <Td>
                    {format(appointment.scheduledCollectionTime, "d MMM, yyyy")}
                  </Td>
                  <Td>
                    <List>
                      {Object.values(appointment.patients).map(
                        (patient: any) => (
                          <ListItem
                            key={patient.id}
                            py={1}
                            textTransform="capitalize"
                          >{`${patient.patientName}: #${patient.id}`}</ListItem>
                        )
                      )}
                    </List>
                  </Td>
                  <Td>
                    <List>
                      {Object.values(appointment.patients).map(
                        (patient: any) => (
                          <ListItem py={1} key={patient.id}>
                            {Object.values(patient.tests)
                              .map((test: any) => test.name)
                              .join(", ")}
                          </ListItem>
                        )
                      )}
                    </List>
                  </Td>
                  <Td>
                    <List>
                      {Object.values(appointment.patients).map(
                        (patient: any) => (
                          <ListItem key={patient.id} py={1}>
                            {getResult(patient)}
                          </ListItem>
                        )
                      )}
                    </List>
                  </Td>
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
}
